import { cloneDeep, omit } from 'lodash';
import { router } from '@/plugins/router';
import {
  NAV_LIST_DEFAULT,
  NAV_LIST_CONFIG_DEFAULT,
  actionCreationQueryParam,
  infoCreationQueryParam,
  pbReportingQueryParam,
  pbSheetQueryParam,
  ongoingGwQueryParam,
  ishikawaModalQueryParam,
  fwModalQueryParam,
  gwCreationQueryParam,
  modalEditionQueryParams,
  confirmLeavingQueryParams,
} from '@/utils/navigation-constants';

/**
 * Create a route link for displaying an edition modal.
 * See createActionEditionLink, createInfoEditionLink for more information.
 * @param  {Number}  dataId
 * @param  {Object}  currentRoute
 * @param  {String}  queryParam
 * @param  {Boolean} [deleteFollowingMeeting=true]
 * @param  {Object} [optionalQueryParams={}] Add specific query params and their values
 * @return {String}
 */
export function createModalEditionLink(
  dataId,
  currentRoute,
  queryParam,
  deleteFollowingMeeting = true,
  optionalQueryParams = {},
) {
  const route = cloneDeep(omit(currentRoute, 'matched'));
  // Prevent overlapping modals
  confirmLeavingQueryParams.forEach((qParam) => {
    delete route.query[qParam];
  });
  route.query[queryParam] = dataId;
  // If the route was pushed while following the meeting, and the meeting isn't being followed anymore,
  // we want to make sure to delete this query param
  if (deleteFollowingMeeting && route.query.followingMeeting) {
    delete route.query.followingMeeting;
  }
  // Optional query params
  Object.keys(optionalQueryParams).forEach((key) => {
    route.query[key] = optionalQueryParams[key];
  });

  const newRoute = router.resolve(route);
  return newRoute.href;
}

/**
 * Create a link for the router to push in order to display the action edition modal.
 * @param  {Number} actionId      ID of the action to edit. 0 to create a new action
 * @param  {Object} currentRoute  Current route displayed
 * @return {String}               Link for the router
 */
export function createActionEditionLink(actionId, currentRoute, deleteFollowingMeeting = true) {
  return createModalEditionLink(actionId, currentRoute, actionCreationQueryParam, deleteFollowingMeeting);
}

/**
 * Create a link for the router to push in order to display the information edition modal.
 * @param  {Number} infoId        ID of the information to edit. 0 to create a new information
 * @param  {Object} currentRoute  Current route displayed
 * @return {String}               Link for the router
 */
export function createInfoEditionLink(infoId, currentRoute, deleteFollowingMeeting = true) {
  return createModalEditionLink(infoId, currentRoute, infoCreationQueryParam, deleteFollowingMeeting);
}

/**  Same as above for a problem */
export function createPbReportingLink(pbId, currentRoute, deleteFollowingMeeting = true, queryParams = {}) {
  return createModalEditionLink(pbId, currentRoute, pbReportingQueryParam, deleteFollowingMeeting, queryParams);
}

/**  Same as above for a problem sheet */
export function createPbSheetLink(pbId, currentRoute, deleteFollowingMeeting = true, queryParams = {}) {
  return createModalEditionLink(pbId, currentRoute, pbSheetQueryParam, deleteFollowingMeeting, queryParams);
}

/**  Same as above for an ongoing GW */
export function createGwOngoingLink(gwId, currentRoute, deleteFollowingMeeting = true, queryParams = {}) {
  return createModalEditionLink(gwId, currentRoute, ongoingGwQueryParam, deleteFollowingMeeting, queryParams);
}

/**  Same as above for an GW creation */
export function createGwCreationLink(gwId, currentRoute, deleteFollowingMeeting = true, queryParams = {}) {
  return createModalEditionLink(gwId, currentRoute, gwCreationQueryParam, deleteFollowingMeeting, queryParams);
}


export default {
  NAV_LIST_DEFAULT,
  NAV_LIST_CONFIG_DEFAULT,
  modalEditionQueryParams,
  confirmLeavingQueryParams,
  actionCreationQueryParam,
  ongoingGwQueryParam,
  infoCreationQueryParam,
  pbReportingQueryParam,
  pbSheetQueryParam,
  ishikawaModalQueryParam,
  fwModalQueryParam,
  gwCreationQueryParam,
  createActionEditionLink,
  createGwCreationLink,
  createGwOngoingLink,
  createInfoEditionLink,
  createPbReportingLink,
  createPbSheetLink,
};
